// src/components/layout/MobileNav.jsx
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  Target,
  BookOpen,
  Sparkles,
  MessageSquare,
  User,
  LogOut,
  Settings,
  Award,
  LogIn,
  X,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const MobileNav = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logOut } = useAuth();

  const navigation = [
    { name: 'Home', href: '/', icon: Home },
    { name: 'Assessment', href: '/assessment', icon: Target },
    { name: 'Dive In', href: '/modules', icon: BookOpen },
    { name: 'Toolkit', href: '/toolkit', icon: Sparkles },
    { name: 'Dashboard', href: '/dashboard', icon: Award },
    { name: 'Resources', href: '/resources', icon: MessageSquare },
  ];

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/');
      onClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Slide-out Menu */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 20 }}
        className="fixed right-0 top-0 bottom-0 w-80 bg-gunmetal shadow-2xl z-50 lg:hidden overflow-y-auto"
      >
        <div className="p-6">
          {/* Close: the drawer sits over the header's menu button */}
          <div className="flex justify-end -mt-2 -mr-2 mb-4">
            <button
              onClick={onClose}
              className="p-2.5 rounded-lg text-pearl hover:text-bright-gold hover:bg-white/10 transition-colors"
              aria-label="Close menu"
            >
              <X className="w-6 h-6" aria-hidden="true" />
            </button>
          </div>

          {/* User Info */}
          {user && (
            <div className="mb-6 p-4 bg-gold/20 rounded-lg">
              <p className="text-pearl font-medium">{user.displayName || 'User'}</p>
              <p className="text-sm text-muted-gold">{user.email}</p>
            </div>
          )}

          {/* Navigation Links */}
          <nav className="space-y-2 mb-6">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gold/20 text-bright-gold'
                      : 'text-pearl hover:text-bright-gold hover:bg-gunmetal/20'
                  }`}
                >
                  <Icon className="w-5 h-5" aria-hidden="true" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Actions */}
          {user ? (
            <div className="space-y-2 pt-6 border-t border-charcoal">
              <Link
                to="/profile"
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-pearl hover:text-bright-gold hover:bg-gunmetal/20 transition-all"
              >
                <User className="w-5 h-5" aria-hidden="true" />
                <span>Profile</span>
              </Link>

              <Link
                to="/settings"
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-pearl hover:text-bright-gold hover:bg-gunmetal/20 transition-all"
              >
                <Settings className="w-5 h-5" aria-hidden="true" />
                <span>Settings</span>
              </Link>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-pearl hover:text-bright-gold hover:bg-gunmetal/20 transition-all text-left"
                aria-label="Log out"
              >
                <LogOut className="w-5 h-5" aria-hidden="true" />
                <span>Log Out</span>
              </button>
            </div>
          ) : (
            <div className="pt-6 border-t border-charcoal">
              <Link
                to="/login"
                onClick={onClose}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gold/20 text-bright-gold hover:bg-gold/30 transition-all font-medium"
              >
                <LogIn className="w-5 h-5" aria-hidden="true" />
                <span>Log In</span>
              </Link>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};

export default MobileNav;